import { useState, useEffect, useRef } from 'react';
import { countriesAPI } from '../services/api';
import { EMPTY_COUNTRY_INFO, normalizeCountryInfo } from '../utils/countryInfoUtils';

async function fetchCountryInfo(isoCode) {
  if (!isoCode) {
    return { ...EMPTY_COUNTRY_INFO };
  }

  try {
    const data = await countriesAPI.getMetadata(isoCode.toUpperCase());
    if (!data?.available) {
      return { ...EMPTY_COUNTRY_INFO };
    }
    return normalizeCountryInfo(data);
  } catch (error) {
    console.warn('Country info metadata unavailable:', error?.message || error);
    return { ...EMPTY_COUNTRY_INFO };
  }
}

export function useCountryInfoData(country) {
  const [info, setInfo] = useState(EMPTY_COUNTRY_INFO);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!country?.iso_code) {
      setInfo(EMPTY_COUNTRY_INFO);
      setLoading(false);
      setIsReady(false);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setIsReady(false);

      try {
        const data = await fetchCountryInfo(country.iso_code);
        if (!cancelled && requestId === requestIdRef.current) {
          setInfo(data);
        }
      } catch {
        if (!cancelled && requestId === requestIdRef.current) {
          setInfo(EMPTY_COUNTRY_INFO);
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
          setIsReady(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [country?.iso_code]);

  return { info, loading, isReady };
}
